import { useState, useEffect } from "react"
import { Center, HStack, Separator, Stack, Switch, VStack } from "@chakra-ui/react"
import PhoneInput from "react-phone-input-2"
import { Button, Input, Text, Toaster, toaster } from "@/components"
import { useAuth } from "@/contexts"
import {
  useClientContactInfo,
  useSendAccountPhoneOtp,
  useSetTwoFactor,
  useUpdateClientContactInfo,
  useActivePendingContactChange,
} from "@/hooks"
import { useVerifyPhone, useResendPhoneOtp } from "@/hooks"
import { CodeTarget, apiErrorMessage, codeSentSentence, codeTargetFrom } from "@/utils"

interface ContactFields {
  email: string
  phone: string
  address: string
  city: string
  state: string
  zip: string
}

const FIELD_LABELS: Record<keyof ContactFields, string> = {
  email: "Email",
  phone: "Phone",
  address: "Address",
  city: "City",
  state: "State",
  zip: "Zip",
}

const emptyFields: ContactFields = { email: "", phone: "", address: "", city: "", state: "", zip: "" }

type AccountStep = "form" | "verify-phone" | "verify-phone-2fa" | "verify-phone-2fa-disable"

export const AccountComponent = () => {
  const { user, refreshUser } = useAuth()
  const { data: contactInfo, isLoading, refetch } = useClientContactInfo()
  const { data: pendingChange, refetch: refetchPending } = useActivePendingContactChange()
  const { mutate: updateContactInfo, isPending: isUpdating } = useUpdateClientContactInfo()
  const { mutateAsync: sendPhoneOtp, isPending: isSendingOtp } = useSendAccountPhoneOtp()
  const { mutateAsync: verifyPhone, isPending: isVerifyingPhone } = useVerifyPhone()
  const { mutateAsync: resendOtp, isPending: isResending } = useResendPhoneOtp()
  const { mutateAsync: setTwoFactor, isPending: isSettingTwoFactor } = useSetTwoFactor()

  const [isEditing, setIsEditing] = useState(false)
  const [original, setOriginal] = useState<ContactFields>(emptyFields)
  const [form, setForm] = useState<ContactFields>(emptyFields)

  // OTP flow state
  const [accountStep, setAccountStep] = useState<AccountStep>("form")
  const [otpCode, setOtpCode] = useState("")
  const [otpError, setOtpError] = useState("")
  const [resendSuccess, setResendSuccess] = useState(false)
  const [phoneLast4, setPhoneLast4] = useState("")
  // Where the code went: a text (our number, or Twilio Verify abroad) or the sign-in email.
  const [codeTarget, setCodeTarget] = useState<CodeTarget>({ channel: "sms", destination: "" })
  // Shown under the two-factor switch when no text can reach the number on file. Inline as
  // well as the toast, so the reason stays after the toast fades.
  const [twoFactorError, setTwoFactorError] = useState("")

  useEffect(() => {
    if (contactInfo) {
      const fields: ContactFields = {
        email: contactInfo.email || "",
        phone: contactInfo.phone || "",
        address: contactInfo.address || "",
        city: contactInfo.city || "",
        state: contactInfo.state || "",
        zip: contactInfo.zip || "",
      }
      setOriginal(fields)
      setForm(fields)
    }
  }, [contactInfo])

  const buildChanges = () => {
    const changes: { field: string; from: string; to: string }[] = []
    for (const key of Object.keys(form) as (keyof ContactFields)[]) {
      if (form[key] !== original[key]) {
        changes.push({ field: FIELD_LABELS[key], from: original[key], to: form[key] })
      }
    }
    return changes
  }

  const submitUpdate = (changes: { field: string; from: string; to: string }[]) => {
    updateContactInfo(
      { email: form.email, phone: form.phone, address: form.address, city: form.city, state: form.state, zip: form.zip, changes },
      {
        onSuccess: () => {
          toaster.create({
            title: "Submitted for review",
            description: "Your equity partner has been emailed to confirm these changes.",
            type: "success",
            duration: 6000,
          })
          setIsEditing(false)
          refetch()
          refetchPending()
        },
        onError: () => {
          toaster.create({ title: "Failed to submit changes. Please try again.", type: "error", duration: 3000 })
        },
      }
    )
  }

  const handleCancel = () => {
    setForm(original)
    setIsEditing(false)
  }

  const handleSubmit = async () => {
    const changes = buildChanges()
    if (changes.length === 0) {
      toaster.create({ title: "No changes detected", type: "info", duration: 3000 })
      return
    }

    // If phone changed, require OTP verification first
    if (original.phone !== form.phone) {
      try {
        const result = await sendPhoneOtp({ phoneNumber: form.phone })
        setPhoneLast4(result.phoneLast4)
        setCodeTarget(codeTargetFrom(result))
        setOtpCode("")
        setOtpError("")
        setResendSuccess(false)
        setAccountStep("verify-phone")
      } catch {
        toaster.create({ title: "Failed to send verification code. Please try again.", type: "error", duration: 3000 })
      }
      return
    }

    submitUpdate(changes)
  }

  const handleVerifyPhone = async () => {
    try {
      setOtpError("")
      await verifyPhone({ otp: otpCode })
      // Phone verified, now submit the update
      const changes = buildChanges()
      submitUpdate(changes)
      setAccountStep("form")
    } catch {
      setOtpError("Invalid or expired code. Please try again.")
    }
  }

  // New-number screen: resend to the NEW number (or by email). The two-factor screens
  // below resend to the number on file.
  const handleResendNewNumber = async (channel?: "email") => {
    try {
      setResendSuccess(false)
      setOtpError("")
      const result = await sendPhoneOtp({ phoneNumber: form.phone, channel: channel ?? (codeTarget.channel === "email" ? "email" : undefined) })
      setCodeTarget(codeTargetFrom(result))
      setResendSuccess(true)
    } catch (error) {
      setOtpError(apiErrorMessage(error) ?? "Failed to resend code. Please try again.")
    }
  }

  // Two-factor on/off has to prove the phone, so this never asks for email.
  const handleResendCode = async () => {
    try {
      setResendSuccess(false)
      const sent = await resendOtp({ purpose: "two_factor" })
      setCodeTarget(codeTargetFrom(sent))
      setResendSuccess(true)
    } catch (error) {
      setOtpError(apiErrorMessage(error) ?? "Failed to resend code. Please try again.")
    }
  }

  // 2FA toggle — always verify phone before changing
  const handle2FAToggle = async () => {
    if (!user || user.userType !== "client") return
    const enabling = user.twoFactorEnabled !== 1
    try {
      setTwoFactorError("")
      const sent = await resendOtp({ purpose: "two_factor" })
      setPhoneLast4(user?.phoneNumber?.slice(-4) || "")
      setCodeTarget(codeTargetFrom(sent))
      setOtpCode("")
      setOtpError("")
      setResendSuccess(false)
      setAccountStep(enabling ? "verify-phone-2fa" : "verify-phone-2fa-disable")
    } catch (error) {
      setTwoFactorError(apiErrorMessage(error) ?? "")
      toaster.create({ title: "Failed to send verification code.", type: "error", duration: 3000 })
    }
  }

  const handleVerifyPhone2FA = async () => {
    try {
      setOtpError("")
      await verifyPhone({ otp: otpCode })
      await setTwoFactor(true)
      await refreshUser()
      toaster.create({ title: "Two-factor authentication enabled", type: "success", duration: 3000 })
      setAccountStep("form")
    } catch {
      setOtpError("Invalid or expired code. Please try again.")
    }
  }

  const handleVerifyPhone2FADisable = async () => {
    try {
      setOtpError("")
      await verifyPhone({ otp: otpCode })
      await setTwoFactor(false)
      await refreshUser()
      toaster.create({ title: "Two-factor authentication disabled", type: "success", duration: 3000 })
      setAccountStep("form")
    } catch {
      setOtpError("Invalid or expired code. Please try again.")
    }
  }

  if (isLoading) {
    return (
      <VStack pt={10} pb={10} w="100%">
        <Text fontSize="lg">Loading account info...</Text>
      </VStack>
    )
  }

  // OTP verification step for phone number change
  if (accountStep === "verify-phone") {
    return (
      <Center w="100%" py={10} minH="100%">
        <VStack w={{ base: "92%", md: "40%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" gap={4}>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">Verify Your New Phone Number</Text>
          <Text fontSize="sm" textAlign="center">
            {codeSentSentence(codeTarget, phoneLast4)} Please enter it below.
          </Text>
          <Input
            placeholder="Enter 6-digit code"
            type="text"
            value={otpCode}
            maxLength={6}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOtpCode(e.target.value)
              setOtpError("")
              setResendSuccess(false)
            }}
          />
          {otpError && <Text fontSize="sm" color="red.500">{otpError}</Text>}
          {resendSuccess && <Text fontSize="sm" color="green.500">A new code has been sent.</Text>}
          <Button
            variant="solid"
            w="100%"
            loading={isVerifyingPhone || isUpdating}
            disabled={isVerifyingPhone || isUpdating || otpCode.length !== 6}
            onClick={handleVerifyPhone}
          >
            Verify & Update
          </Button>
          <Text
            fontSize="sm"
            color="blue.500"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={!isSendingOtp ? () => handleResendNewNumber() : undefined}
          >
            {isSendingOtp ? "Sending..." : "Resend Code"}
          </Text>
          {/* Only a Twilio Verify text (a number outside the US and Canada) offers email. */}
          {codeTarget.channel === "verify" && (
            <Text
              fontSize="sm"
              color="gray.600"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
              onClick={!isSendingOtp ? () => handleResendNewNumber("email") : undefined}
            >
              Didn't get a text? Email me a code instead
            </Text>
          )}
          <Text
            fontSize="sm"
            color="gray.500"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={() => setAccountStep("form")}
          >
            Back
          </Text>
        </VStack>
        <Toaster />
      </Center>
    )
  }

  // OTP verification step for 2FA enable/disable
  if (accountStep === "verify-phone-2fa" || accountStep === "verify-phone-2fa-disable") {
    const isEnabling = accountStep === "verify-phone-2fa"
    return (
      <Center w="100%" py={10} minH="100%">
        <VStack w={{ base: "92%", md: "40%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" gap={4}>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">Verify Your Phone Number</Text>
          <Text fontSize="sm" textAlign="center">
            To {isEnabling ? "enable" : "disable"} two-factor authentication, please verify your phone number. We sent a 6-digit code to the number ending in {phoneLast4}.
          </Text>
          <Input
            placeholder="Enter 6-digit code"
            type="text"
            value={otpCode}
            maxLength={6}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOtpCode(e.target.value)
              setOtpError("")
              setResendSuccess(false)
            }}
          />
          {otpError && <Text fontSize="sm" color="red.500">{otpError}</Text>}
          {resendSuccess && <Text fontSize="sm" color="green.500">A new code has been sent.</Text>}
          <Button
            variant="solid"
            w="100%"
            loading={isVerifyingPhone || isSettingTwoFactor}
            disabled={isVerifyingPhone || isSettingTwoFactor || otpCode.length !== 6}
            onClick={isEnabling ? handleVerifyPhone2FA : handleVerifyPhone2FADisable}
          >
            Verify & {isEnabling ? "Enable" : "Disable"} 2FA
          </Button>
          <Text
            fontSize="sm"
            color="blue.500"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={!isResending ? handleResendCode : undefined}
          >
            {isResending ? "Sending..." : "Resend Code"}
          </Text>
          <Text
            fontSize="sm"
            color="gray.500"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={() => setAccountStep("form")}
          >
            Back
          </Text>
        </VStack>
        <Toaster />
      </Center>
    )
  }

  // While a pending change is awaiting equity-partner approval, the form is
  // read-only and the Edit button is hidden.
  const hasPendingChange = !!pendingChange && pendingChange.status === "pending"
  const editLocked = hasPendingChange
  const isFormEditable = isEditing && !editLocked

  return (
    <VStack w="100%" align="stretch">
      <VStack pt={10} pb={10} bg="gray.100" w="100%">
        <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="bold">
          Account
        </Text>
        <Text fontSize="md" color="gray.500">
          View and update your contact information
        </Text>
      </VStack>

      <Stack px={{ base: 4, md: 20 }} py={10} gap={6} w="100%">
        {hasPendingChange && (
          <Stack
            p={5}
            borderWidth="1px"
            borderRadius="lg"
            borderColor="orange.300"
            bg="orange.50"
            gap={2}
          >
            <Text fontSize="md" fontWeight="semibold" color="orange.800">
              Your account information changes are under review for your security
            </Text>
            <Text fontSize="sm" color="gray.700">
              Please contact your equity partner{pendingChange?.equityPartnerEmail ? ` (${pendingChange.equityPartnerEmail})` : ""} to confirm these changes were intended.
              They will receive an email asking them to approve your request, and your account is locked from further edits until they do.
            </Text>
            {pendingChange?.changes && pendingChange.changes.length > 0 && (
              <Stack mt={2} gap={1}>
                {pendingChange.changes.map((c) => (
                  <Text key={c.field} fontSize="sm" color="gray.700">
                    <Text as="span" fontWeight="semibold">{c.field}:</Text>{" "}
                    <Text as="span" color="gray.500" textDecoration="line-through">{c.from || "(empty)"}</Text>{" "}
                    {"\u2192"}{" "}
                    <Text as="span" color="orange.800" fontWeight="medium">{c.to || "(empty)"}</Text>
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        )}
        <Stack direction={{ base: "column", md: "row" }} gap={4} w="100%">
          <Stack flex={1} gap={1}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">Email</Text>
            <Input
              value={form.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              readOnly={!isFormEditable}
              bg={isFormEditable ? "white" : "gray.50"}
              borderColor={isFormEditable ? "blue.300" : "gray.200"}
              cursor={isFormEditable ? "text" : "default"}
            />
          </Stack>
          <Stack flex={1} gap={1}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">Phone</Text>
            <PhoneInput
              country={"us"}
              value={form.phone}
              onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
              disabled={!isFormEditable}
              inputProps={{
                name: "phone",
                autoComplete: "off",
              }}
              inputStyle={{
                fontFamily: "Nunito Sans",
                width: "100%",
                backgroundColor: isFormEditable ? "white" : "#F7FAFC",
                cursor: isFormEditable ? "text" : "default",
              }}
            />
          </Stack>
        </Stack>
        {([["address", "city"], ["state", "zip"]] as [keyof ContactFields, keyof ContactFields][]).map(([left, right]) => (
          <Stack direction={{ base: "column", md: "row" }} key={left} gap={4} w="100%">
            <Stack flex={1} gap={1}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                {FIELD_LABELS[left]}
              </Text>
              <Input
                value={form[left]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((prev) => ({ ...prev, [left]: e.target.value }))
                }
                readOnly={!isFormEditable}
                bg={isFormEditable ? "white" : "gray.50"}
                borderColor={isFormEditable ? "blue.300" : "gray.200"}
                cursor={isFormEditable ? "text" : "default"}
              />
            </Stack>
            <Stack flex={1} gap={1}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                {FIELD_LABELS[right]}
              </Text>
              <Input
                value={form[right]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((prev) => ({ ...prev, [right]: e.target.value }))
                }
                readOnly={!isFormEditable}
                bg={isFormEditable ? "white" : "gray.50"}
                borderColor={isFormEditable ? "blue.300" : "gray.200"}
                cursor={isFormEditable ? "text" : "default"}
              />
            </Stack>
          </Stack>
        ))}

        <HStack gap={3} pt={4}>
          {editLocked ? null : !isFormEditable ? (
            <Button onClick={() => setIsEditing(true)}>Edit</Button>
          ) : (
            <>
              <Button onClick={handleSubmit} disabled={isUpdating || isSendingOtp}>
                {isUpdating ? "Submitting..." : isSendingOtp ? "Sending code..." : "Submit"}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isUpdating || isSendingOtp}>
                Cancel
              </Button>
            </>
          )}
        </HStack>

        <Separator />

        {/* 2FA Toggle — clients only */}
        {user?.userType === "client" && (
          <Stack gap={2}>
            <Text fontSize="md" fontWeight="bold">Security</Text>
            <HStack justifyContent="space-between" p={4} borderRadius={10} bg="gray.50">
              <Stack gap={0}>
                <Text fontSize="sm" fontWeight="semibold">Two-Factor Authentication</Text>
                <Text fontSize="xs" color="gray.500">
                  When enabled, you will receive a verification code via SMS each time you log in.
                </Text>
                {twoFactorError && <Text fontSize="xs" color="red.500">{twoFactorError}</Text>}
              </Stack>
              <Switch.Root
                checked={user.twoFactorEnabled === 1}
                onCheckedChange={handle2FAToggle}
                disabled={isSettingTwoFactor || isResending}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </HStack>
          </Stack>
        )}
      </Stack>
      <Toaster />
    </VStack>
  )
}
